import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/v5/lib/api";

export interface CRMStage {
  accountCustomStageID: string;
  stageName: string;
  stageID: string;
  orderNum: string;
  planCount: number;
}

export interface CRMPlan {
  planID: string;
  displayID: string;
  patientID: string;
  patientFirstName: string;
  patientLastName: string;
  facility: string;
  planTypeName: string;
  interestLevel: string;
  procName: string;
  lastContact: string;
  lcType: string;
  /**
   * ISO-8601 instant the plan entered its current stage, derived server-side
   * from the comm_log typeID=17 stage-change rows. Null when unknown — the
   * plan has no surviving stage-change row, or the newest one disagrees with
   * its current stage. Do not substitute `lastContact` for this; that column
   * is stamped by both real outreach and every stage change.
   */
  stageSince: string | null;
  nextAppt: string;
  docName: string;
  coordName: string;
  orderDate: string;
  stageName: string;
  status: string;
  patientEmail: string;
  patientPhone: string;
  patientCity: string;
}

interface LaravelStagesResponse {
  data: { stages: CRMStage[]; newLeadsCount: number };
  meta: unknown;
}

interface LaravelPlansResponse {
  data: { plans: CRMPlan[]; totalCount: number };
  meta: unknown;
}

/**
 * `enabled` is how a caller holds the query until its saved filters have
 * loaded. Without it the page fires once unfiltered on mount and again a
 * round-trip later, and the counts it shows are whichever response happens to
 * land last. `loading` stays true while disabled, so a gated caller renders its
 * loading state rather than an empty one.
 */
export function useCRMStages(filtersArray = "", filterPlus = "", enabled = true) {
  const [stages, setStages] = useState<CRMStage[]>([]);
  const [newLeadsCount, setNewLeadsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const requestIdRef = useRef(0);

  const refetch = useCallback(async () => {
    // Overlapping requests still happen (a filter changed mid-flight), so the
    // newest one owns the state no matter the order the responses arrive in.
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (filtersArray) params.filtersArray = filtersArray;
      if (filterPlus) params.filterPlus = filterPlus;
      const result = await api<LaravelStagesResponse>("/crm/stages", {
        params: Object.keys(params).length ? params : undefined,
      });
      if (requestId !== requestIdRef.current) return;
      if (result.data?.stages) {
        setStages(result.data.stages);
      }
      setNewLeadsCount(result.data?.newLeadsCount ?? 0);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      console.error("Failed to fetch CRM stages:", err);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [filtersArray, filterPlus]);

  useEffect(() => {
    if (!enabled) return;
    refetch();
  }, [enabled, refetch]);

  return { stages, newLeadsCount, loading, refetch };
}

const PAGE_SIZE = 25;

export function useCRMPlans(stageID: string | null, legacyStageID: string | null, enabled = true, filtersArray = "", filterPlus = "") {
  const [plans, setPlans] = useState<CRMPlan[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const offsetRef = useRef(0);
  const fetchingRef = useRef(false);
  const requestIdRef = useRef(0);

  const fetchPage = useCallback(
    async (start: number, append: boolean) => {
      if (!stageID) return;
      // See useCRMLeads: the in-flight guard is only there to stop `loadMore`
      // appending a page twice. A reset carries new filters and must not be
      // dropped just because the mount's unfiltered request is still open.
      if (append && fetchingRef.current) return;
      fetchingRef.current = true;
      const requestId = ++requestIdRef.current;

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      try {
        const body: Record<string, unknown> = {
          stageID,
          legacyStageID: legacyStageID || "0",
          displayStart: start,
          displayLength: PAGE_SIZE,
        };
        if (filtersArray) body.filtersArray = filtersArray;
        if (filterPlus) body.filterPlus = filterPlus;

        const result = await api<LaravelPlansResponse>("/crm/plans", {
          method: "POST",
          body,
        });
        if (requestId !== requestIdRef.current) return;
        if (result.data?.plans) {
          setPlans((prev) => (append ? [...prev, ...result.data.plans] : result.data.plans));
          setTotalCount(result.data.totalCount ?? 0);
          offsetRef.current = start + result.data.plans.length;
        }
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        console.error("Failed to fetch CRM plans:", err);
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
          fetchingRef.current = false;
        }
      }
    },
    [stageID, legacyStageID, filtersArray, filterPlus],
  );

  const loadMore = useCallback(() => {
    fetchPage(offsetRef.current, true);
  }, [fetchPage]);

  const refetch = useCallback(() => {
    offsetRef.current = 0;
    setPlans([]);
    fetchPage(0, false);
  }, [fetchPage]);

  useEffect(() => {
    if (!enabled || !stageID) return;
    offsetRef.current = 0;
    setPlans([]);
    setTotalCount(0);
    fetchPage(0, false);
  }, [stageID, enabled, fetchPage]);

  const hasMore = plans.length < totalCount;

  return { plans, totalCount, loading, loadingMore, hasMore, loadMore, refetch };
}
